import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Title, 
  ContentContainer
} from '../components/HeroSectionStyles';
import ParticlesBackground from '../components/ParticlesBackground';

// Animations from BemVindo.jsx
const glowPulse = keyframes`
  0% { text-shadow: 0 0 10px rgba(100, 182, 255, 0.5), 0 0 20px rgba(100, 182, 255, 0.3); }
  50% { text-shadow: 0 0 15px rgba(100, 182, 255, 0.8), 0 0 30px rgba(100, 182, 255, 0.5), 0 0 40px rgba(0, 255, 255, 0.3); }
  100% { text-shadow: 0 0 10px rgba(100, 182, 255, 0.5), 0 0 20px rgba(100, 182, 255, 0.3); }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const rotateGlow = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const scanLine = keyframes`
  0% { transform: translateY(-100%); }
  100% { transform: translateY(100%); }
`;

const clientGradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const textReveal = keyframes`
  0% { clip-path: polygon(0 0, 0 0, 0 100%, 0 100%); }
  100% { clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%); }
`;

// Page transition animations
const slideInFromRight = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideOutToLeft = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(-40px); }
`;

const slideOutToRight = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(40px); }
`;

// Styled Components
const OnboardingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100%;
  padding: 20px;
  overflow: hidden;
  position: relative;
  
  &.enter-right { animation: ${slideInFromRight} 280ms ease-out both; }
  &.enter-left { animation: ${slideInFromLeft} 280ms ease-out both; }
  &.exit-left { animation: ${slideOutToLeft} 240ms ease-in both; }
  &.exit-right { animation: ${slideOutToRight} 240ms ease-in both; }
`;

const ContentPreview = styled(ContentContainer)`
  max-width: 1200px;
  width: 90%;
  margin: 0 auto;
  padding: 60px;
  border: 1px solid rgba(100, 182, 255, 0.3);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.2);
  box-shadow: 0 0 20px rgba(100, 182, 255, 0.1);
  backdrop-filter: blur(10px);
  position: relative;
  transform: scale(1.2) !important;
  will-change: transform;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(100, 182, 255, 0.8), transparent);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(100, 182, 255, 0.8), transparent);
  }
`;

const TitleNoGlitch = styled(Title)`
  .title-text::after {
    content: none !important;
    display: none !important;
  }
`;

const WelcomeText = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.5rem;
  margin: 2rem 0;
  color: #64B6FF;
  text-align: center;
  letter-spacing: 2px;
  text-shadow: 0 0 10px rgba(100, 182, 255, 0.5);
  animation: ${fadeIn} 1s ease-out forwards;
`;

const ClientNameContainer = styled.div`
  position: relative;
  margin: 1.5rem 0 2.5rem;
  padding: 1rem;
  width: auto;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  transform-style: preserve-3d;
  perspective: 1000px;
`;

const ClientName = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 3rem;
  font-weight: 700;
  padding: 0.8rem 2.5rem;
  background: linear-gradient(90deg, 
    rgba(0, 150, 255, 0.2), 
    rgba(0, 255, 255, 0.4), 
    rgba(0, 150, 255, 0.2));
  background-size: 200% 100%;
  animation: 
    ${clientGradient} 5s ease infinite, 
    ${textReveal} 1s ease forwards;
  border-radius: 8px;
  position: relative;
  color: #fff;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.7), 0 0 20px rgba(0, 255, 255, 0.5);
  letter-spacing: 2px;
  display: inline-block;
  transform: perspective(500px) translateZ(0px);
  transition: all 0.3s ease;
  overflow: hidden;
  box-shadow: 
    0 0 20px rgba(0, 150, 255, 0.2),
    inset 0 0 15px rgba(0, 150, 255, 0.2);
  border: 1px solid rgba(0, 255, 255, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 10px;
    background: linear-gradient(45deg, #00ffff, #0096ff, #00ffff);
    opacity: 0.5;
    z-index: -1;
    filter: blur(2px);
  }
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    height: 100%;
    width: 100%;
    background: linear-gradient(0deg, 
      transparent 0%, 
      rgba(0, 255, 255, 0.2) 50%, 
      transparent 100%);
    opacity: 0.5;
    animation: ${scanLine} 4s linear infinite;
    z-index: 1;
    pointer-events: none;
  }
`;

const TechCircle = styled.div`
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  border: 1px solid rgba(100, 182, 255, 0.3);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: -1;
  opacity: 0.5;
  pointer-events: none;
  
  &::before {
    content: '';
    position: absolute;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    border: 1px dashed rgba(0, 255, 255, 0.3);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    animation: ${rotateGlow} 60s linear infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    border: 2px solid rgba(100, 182, 255, 0.2);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    animation: ${rotateGlow} 30s linear infinite reverse;
  }
`;

// Helper to extract client name from either '?NOMEDOCLIENTE=...' or an unnamed query like '?Camargo%20Imobiliária'
function extractClientName(search) {
  if (!search) return '';
  const params = new URLSearchParams(search);
  const named = params.get('NOMEDOCLIENTE');
  if (named) return named;
  // Handle unnamed single query (no '=')
  const raw = search.startsWith('?') ? search.slice(1) : search;
  if (raw && !raw.includes('=')) {
    try {
      return decodeURIComponent(raw.replace(/\+/g, ' '));
    } catch {
      return raw;
    }
  }
  // Handle entries whose value is empty (e.g., '?Camargo%20Imobiliária')
  for (const [k, v] of params.entries()) {
    if (!v && k) {
      try {
        return decodeURIComponent(k.replace(/\+/g, ' '));
      } catch {
        return k;
      }
    }
  }
  return '';
}

function ReuniaoOnboarding() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract the client name from URL query (supports '?NOMEDOCLIENTE=...' and unnamed '?Name')
  const clientName = extractClientName(location.search);
  
  // Initial animation based on navigation direction
  const [anim, setAnim] = useState(() => (location.state?.dir === 'back' ? 'enter-left' : 'enter-right'));
  
  // Navigate to objetivos page on ArrowRight
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') {
        setAnim('exit-left');
        setTimeout(() => {
          navigate(`/reuniao-onboarding/objetivos${location.search}`, { state: { dir: 'forward' } });
        }, 230);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [navigate, location.search]);
  
  return (
    <>
      <ParticlesBackground />
      <OnboardingWrapper className={anim}>
        <TechCircle />
        <ContentPreview>
          <TitleNoGlitch>
            <div className="logo-container">
              <div className="logo-tech-circle"></div>
              <div className="logo-tech-data"></div>
              <img src="/favicon.png" alt="Logo Bianca" className="title-logo" />
            </div>
            <div className="title-text-container">
              <div className="tech-data-top">// AI.AGENT</div>
              <span className="title-text">Bianca + Journey</span>
              <div className="tech-data-bottom">{/* data.transform(leads) */}</div>
            </div>
          </TitleNoGlitch>
          
          <WelcomeText>Bem Vindo</WelcomeText>
          
          {clientName && (
            <ClientNameContainer>
              <ClientName>{clientName}</ClientName>
            </ClientNameContainer>
          )}
        </ContentPreview>
      </OnboardingWrapper>
    </>
  );
}

export default ReuniaoOnboarding;
